import type {MaybeRef} from 'vue';
import type {ReadonlyDeep} from 'type-fest';
import {getCommonStorySitemapParams} from '~/storyblok/api/options';
import {
	BRANCH_AVAILABILITY_FAMILY_ROOTS,
	BRANCH_AVAILABILITY_SOURCES,
	DEFAULT_BRANCH_PAGE_AVAILABILITY,
	GLOBAL_BRANCH_SLUG,
} from '#shared/constants';
import type {
	BranchAvailabilityFamily,
	BranchAvailabilityStory,
	BranchPageAvailability,
	BranchPageAvailabilityKey,
} from '#shared/types/branchAvailability';

export interface UseBranchPageAvailabilityReturn {
	data: ReadonlyDeep<Ref<BranchPageAvailability>>
	loading: ReadonlyDeep<Ref<boolean>>
}

export interface UseBranchesPageAvailabilityReturn {
	data: ReadonlyDeep<Ref<Record<string, BranchPageAvailability>>>
	loading: ReadonlyDeep<Ref<Record<string, boolean>>>
}

const familyAvailabilityRequests = new Map<BranchAvailabilityFamily, Promise<void>>();

export default function useBranchPageAvailability(branch: MaybeRef<string | null | undefined>): UseBranchPageAvailabilityReturn {
	const branchSlug = computed(() => unref(branch) ?? GLOBAL_BRANCH_SLUG);
	const branchSlugs = computed(() => branchSlug.value ? [branchSlug.value] : []);
	const {data, loading} = useBranchesPageAvailability(branchSlugs);

	return {
		data: computed(() => data.value[branchSlug.value] ?? DEFAULT_BRANCH_PAGE_AVAILABILITY),
		loading: computed(() => loading.value[branchSlug.value] ?? false),
	};
}

export function useBranchesPageAvailability(branches: MaybeRef<(string | null | undefined)[]>): UseBranchesPageAvailabilityReturn {
	const {$storyblokApi} = useNuxtApp();
	const availabilityState = useBranchPageAvailabilityState();
	const branchSlugs = computed(() => [...new Set(
		unref(branches)
			.map((branch) => branch ?? '')
			.filter(Boolean),
	)]);
	const requiredFamilies = computed(() => [...new Set(
		branchSlugs.value.map(getBranchAvailabilityFamily),
	)]);

	watch(requiredFamilies, (families) => {
		families.forEach((family) => {
			fetchFamilyAvailability($storyblokApi, availabilityState, family);
		});
	}, {immediate: true});

	const data = computed(() => Object.fromEntries(
		branchSlugs.value.map((branchSlug) => [branchSlug, availabilityState.availabilityByBranch.value[branchSlug] ?? DEFAULT_BRANCH_PAGE_AVAILABILITY]),
	));
	const loading = computed(() => Object.fromEntries(
		branchSlugs.value.map((branchSlug) => {
			const family = getBranchAvailabilityFamily(branchSlug);
			return [branchSlug, availabilityState.loadingByFamily.value[family] ?? false];
		}),
	));

	return {
		data: readonly(data),
		loading: readonly(loading),
	};
}

function useBranchPageAvailabilityState() {
	const availabilityByBranch = useState<Record<string, BranchPageAvailability>>(
		'branch-page-availability-map',
		() => ({}),
	);
	const loadingByFamily = useState<Record<BranchAvailabilityFamily, boolean>>(
		'branch-page-availability-family-loading-map',
		() => ({
			hypermarket: false,
			fresh: false,
		}),
	);
	const fetchedFamilies = useState<Record<BranchAvailabilityFamily, boolean>>(
		'branch-page-availability-family-fetched-map',
		() => ({
			hypermarket: false,
			fresh: false,
		}),
	);

	return {
		availabilityByBranch,
		loadingByFamily,
		fetchedFamilies,
	};
}

async function fetchFamilyAvailability(
	$storyblokApi: ReturnType<typeof useNuxtApp>['$storyblokApi'],
	state: ReturnType<typeof useBranchPageAvailabilityState>,
	family: BranchAvailabilityFamily,
) {
	if (state.fetchedFamilies.value[family]) {
		return;
	}

	const pendingRequest = familyAvailabilityRequests.get(family);

	if (pendingRequest) {
		await pendingRequest;
		return;
	}

	state.loadingByFamily.value[family] = true;

	const request = (async () => {
		try {
			if (import.meta.client && process.env.NODE_ENV === 'development') {
				console.info('Branch availability fetch started', {family});
			}

			const familyRoot = BRANCH_AVAILABILITY_FAMILY_ROOTS[family];
			const storyParams = getStoryListParams();
			const responses = await Promise.all(
				BRANCH_AVAILABILITY_SOURCES.map(async (source) => {
					const stories = await $storyblokApi.getAll('stories', {
						...storyParams,
						starts_with: `${familyRoot}/`,
						content_type: source.contentType,
						is_startpage: true,
						excluding_fields: 'content,body,openingHours',
						per_page: 100,
					}) as BranchAvailabilityStory[];

					return {
						key: source.key,
						stories,
					};
				}),
			);
			const availabilityByBranch = resolveFamilyAvailability(familyRoot, responses);

			if (import.meta.client && process.env.NODE_ENV === 'development') {
				console.info('Branch availability fetch resolved', {
					family,
					counts: Object.fromEntries(
						responses.map((response) => [response.key, response.stories.length]),
					),
					branches: Object.keys(availabilityByBranch).length,
				});
			}

			state.availabilityByBranch.value = {
				...state.availabilityByBranch.value,
				...availabilityByBranch,
			};
			state.fetchedFamilies.value[family] = true;
		} catch (error) {
			console.error('Failed to resolve branch page availability family', {
				family,
				error,
			});

			state.fetchedFamilies.value[family] = true;
		} finally {
			state.loadingByFamily.value[family] = false;
			familyAvailabilityRequests.delete(family);
		}
	})();

	familyAvailabilityRequests.set(family, request);
	await request;
}

function getStoryListParams() {
	const params = getCommonStorySitemapParams();

	return {
		version: params.version,
		...(params.from_release && {from_release: params.from_release}),
		...(params._storyblok && {_storyblok: params._storyblok}),
		...(params['_storyblok_tk[space_id]'] && {'_storyblok_tk[space_id]': params['_storyblok_tk[space_id]']}),
		...(params['_storyblok_tk[timestamp]'] && {'_storyblok_tk[timestamp]': params['_storyblok_tk[timestamp]']}),
		...(params['_storyblok_tk[token]'] && {'_storyblok_tk[token]': params['_storyblok_tk[token]']}),
	};
}

function resolveFamilyAvailability(
	familyRoot: string,
	responses: Array<{key: BranchPageAvailabilityKey; stories: BranchAvailabilityStory[]}>,
): Record<string, BranchPageAvailability> {
	const availabilityByBranch: Record<string, BranchPageAvailability> = {};

	responses.forEach(({key, stories}) => {
		stories.forEach((story) => {
			const branchSlug = resolveBranchSlugFromFullSlug(familyRoot, story.full_slug);

			if (!branchSlug) {
				return;
			}

			const branchAvailability = availabilityByBranch[branchSlug] ?? {...DEFAULT_BRANCH_PAGE_AVAILABILITY};
			branchAvailability[key] = true;
			availabilityByBranch[branchSlug] = branchAvailability;
		});
	});

	return availabilityByBranch;
}

function resolveBranchSlugFromFullSlug(familyRoot: string, fullSlug?: string) {
	if (!fullSlug) {
		return '';
	}

	const normalizedRoot = familyRoot.replace(/\/+$/, '');
	const normalizedFullSlug = fullSlug.replace(/\/+$/, '');

	if (!normalizedFullSlug.startsWith(`${normalizedRoot}/`)) {
		return '';
	}

	return normalizedFullSlug.slice(normalizedRoot.length + 1).split('/')[0] ?? '';
}

function getBranchAvailabilityFamily(branchSlug: string): BranchAvailabilityFamily {
	return branchSlug.startsWith('fresh-') ? 'fresh' : 'hypermarket';
}
